// Arabic / English copy for the delivery documentation page (/auth/delivery-documentation).
// Arabic is the default (and what renders without the app context, e.g. in component tests). Evidence,
// conclusion and reason labels keep their Arabic source in utils/cashier/deliveryDocumentation (shared
// with the admin pages); the English labels are looked up by the same keys.
import { useContext } from "react";
import { Context } from "../../context/Context";
import {
  CONCLUSION_OPTIONS,
  EVIDENCE_TYPES,
  OUTCOME_LABELS,
  conclusionLabel,
  conclusionReasonLabel,
} from "../../utils/cashier/deliveryDocumentation";

const ar = {
  title: "توثيق الطلبات",
  subtitle: "صوّر الطلبات والفواتير وتابع حالة التوثيق.",
  newOrder: "طلب جديد",
  currentBranch: "الفرع الحالي",
  store: "المتجر",
  branch: "الفرع",
  noBranches: "لا توجد فروع متاحة",
  noStoreBranches: "لا توجد فروع متاحة لهذا المتجر",
  restrictedTitle: "لا توجد فروع مسندة إليك",
  restrictedBody: "لا يمكنك توثيق الطلبات قبل إسناد فرع واحد على الأقل إلى حسابك. تواصل مع مدير المتجر أو فريق لود.",
  noBranchForOrder: "لا يوجد فرع متاح لإنشاء الطلب",
  searchPlaceholder: "بحث بآخر 4 أرقام",
  search: "بحث",
  previous: "السابق",
  next: "التالي",
  loading: "جاري التحميل…",
  retry: "إعادة المحاولة",
  emptyToday: "لا توجد عناصر هنا اليوم.",
  requestFailed: "تعذر إكمال الطلب",
  forbidden: "لا تملك صلاحية لهذا الإجراء أو لهذا الفرع.",
  scopeUnavailable: "الفرع المحدد غير متاح لحسابك.",
  notFound: "العنصر غير موجود أو لا يتبع فروعك.",
  windowExpired: "انتهت مهلة التعديل (24 ساعة).",
  orderCreated: "تم إنشاء الطلب وحفظه",
  similarOrder: "تم إنشاء الطلب. يوجد طلب مشابه خلال آخر 30 دقيقة.",
  noPendingPhoto: "لا توجد صورة بانتظار الرفع",
  photoReplaced: "تم استبدال الصورة",
  photoUploaded: "تم رفع الصورة",
  writeReason: "اكتب السبب",
  deletePhotoConfirm: "حذف الصورة مع حفظها في السجل؟",
  deletePhotoReason: "حذف بواسطة الكاشير",
  photoDeleted: "تم حذف الصورة",
  conclusionReverted: "تم التراجع عن الاستنتاج",
  deleteOrderReason: "سبب حذف الطلب",
  orderDeleted: "تم حذف الطلب",
  conclusionAndNote: "تمت إضافة الاستنتاج والملاحظة",
  conclusionAdded: "تمت إضافة الاستنتاج",
  noteAdded: "تمت إضافة الملاحظة",
  summary: { total: "طلبات اليوم", complete: "مكتملة", pending: "قيد التوثيق", deleted: "المحذوفات" },
  tabs: { pending: "قيد التوثيق", complete: "مكتملة", deleted: "المحذوفات" },
  // order card
  complete: "مكتمل",
  pending: "قيد التوثيق",
  created: "إنشاء",
  windowClosed: "انتهت مهلة التعديل",
  optional: "اختياري",
  deletePhoto: "حذف الصورة",
  outcomeAndNotes: "النتيجة والملاحظات",
  deleteOrder: "حذف الطلب",
  reverting: "جارٍ التراجع...",
  revert: "تراجع",
  confirmRevert: "تأكيد التراجع",
  revertQuestion: (label) => `هل تريد التراجع عن "${label}"؟`,
  yesRevert: "نعم، تراجع",
  cancel: "إلغاء",
  // create order
  last4Label: "آخر 4 أرقام من رقم الطلب",
  digit: (n) => `الرقم ${n}`,
  application: "التطبيق",
  chooseApplication: "اختر التطبيق",
  noApplications: "لا توجد تطبيقات متاحة",
  noApplicationsHint: "لا يمكن إنشاء طلب لهذا الفرع قبل تفعيل تطبيق توصيل.",
  adding: "جاري الإضافة…",
  addOrder: "إضافة الطلب",
  // outcome sheet
  outcomeTitle: (last4) => `النتيجة والاستنتاجات #${last4}`,
  addConclusionOrNote: "إضافة استنتاج أو ملاحظة",
  conclusion: "الاستنتاج",
  noConclusion: "بدون استنتاج",
  allConclusionsAdded: "كل الاستنتاجات المتاحة مضافة حاليًا. يمكن إضافة المزيد بعد التراجع عن استنتاج.",
  reason: "السبب",
  chooseReason: "اختر السبب",
  note: "ملاحظة",
  chooseConclusionOrNote: "اختر استنتاجًا أو اكتب ملاحظة",
  chooseConclusionReason: "اختر سبب الاستنتاج",
  close: "إغلاق",
  saving: "جارٍ الحفظ...",
  save: "حفظ",
  takePhoto: "التقاط صورة",
  photoClearQuestion: "هل الصورة واضحة ومناسبة للتوثيق؟",
  uploading: "جارٍ الرفع...",
  yesSave: "نعم، حفظ",
  // camera
  camera: {
    appInvoice: "فاتورة التطبيق",
    packedOrder: "الطلب المغلف",
    invoice: "الفاتورة",
    phone: "جوال",
    order: "الطلب",
    startFailed: "تعذر تشغيل الكاميرا. اختر صورة من الجهاز.",
    prepareFailed: "تعذر تجهيز الصورة للرفع. حاول التقاطها مرة أخرى.",
    uploadFailed: "تعذر رفع الصورة. حاول مرة أخرى.",
    confirmPhoto: "تأكيد الصورة",
    guide: "دليل التصوير",
    preview: (label) => `معاينة ${label}`,
    progress: (current, total) => `الصورة ${current} من ${total}`,
    retake: "إعادة التصوير",
    starting: "جارٍ تشغيل الكاميرا...",
    gallery: "▧ الصور",
    switch: "↻ تبديل",
  },
};

const en = {
  title: "Order documentation",
  subtitle: "Photograph orders and invoices and track documentation status.",
  newOrder: "New order",
  currentBranch: "Current branch",
  store: "Store",
  branch: "Branch",
  noBranches: "No branches available",
  noStoreBranches: "No branches available for this store",
  restrictedTitle: "No branches assigned to you",
  restrictedBody: "You can document orders once at least one branch is assigned to your account. Contact your store manager or the LUD team.",
  noBranchForOrder: "No branch is available to create the order",
  searchPlaceholder: "Search by last 4 digits",
  search: "Search",
  previous: "Previous",
  next: "Next",
  loading: "Loading…",
  retry: "Try again",
  emptyToday: "Nothing here today.",
  requestFailed: "The request could not be completed",
  forbidden: "You do not have permission for this action or branch.",
  scopeUnavailable: "The selected branch is not available to your account.",
  notFound: "This item does not exist or does not belong to your branches.",
  windowExpired: "The 24-hour editing window has ended.",
  orderCreated: "Order created and saved",
  similarOrder: "Order created. A similar order exists from the last 30 minutes.",
  noPendingPhoto: "No photo is waiting to be uploaded",
  photoReplaced: "Photo replaced",
  photoUploaded: "Photo uploaded",
  writeReason: "Write the reason",
  deletePhotoConfirm: "Delete the photo (it stays in the history)?",
  deletePhotoReason: "Deleted by branch staff",
  photoDeleted: "Photo deleted",
  conclusionReverted: "Conclusion reverted",
  deleteOrderReason: "Reason for deleting the order",
  orderDeleted: "Order deleted",
  conclusionAndNote: "Conclusion and note added",
  conclusionAdded: "Conclusion added",
  noteAdded: "Note added",
  summary: { total: "Today's orders", complete: "Complete", pending: "In progress", deleted: "Deleted" },
  tabs: { pending: "In progress", complete: "Complete", deleted: "Deleted" },
  complete: "Complete",
  pending: "In progress",
  created: "Created",
  windowClosed: "Editing window ended",
  optional: "Optional",
  deletePhoto: "Delete photo",
  outcomeAndNotes: "Outcome & notes",
  deleteOrder: "Delete order",
  reverting: "Reverting...",
  revert: "Revert",
  confirmRevert: "Confirm revert",
  revertQuestion: (label) => `Revert "${label}"?`,
  yesRevert: "Yes, revert",
  cancel: "Cancel",
  last4Label: "Last 4 digits of the order number",
  digit: (n) => `Digit ${n}`,
  application: "Application",
  chooseApplication: "Choose the application",
  noApplications: "No applications available",
  noApplicationsHint: "Orders cannot be created for this branch until a delivery application is active.",
  adding: "Adding…",
  addOrder: "Add order",
  outcomeTitle: (last4) => `Outcome & conclusions #${last4}`,
  addConclusionOrNote: "Add a conclusion or note",
  conclusion: "Conclusion",
  noConclusion: "No conclusion",
  allConclusionsAdded: "Every available conclusion is already added. Revert one to add another.",
  reason: "Reason",
  chooseReason: "Choose the reason",
  note: "Note",
  chooseConclusionOrNote: "Choose a conclusion or write a note",
  chooseConclusionReason: "Choose the conclusion reason",
  close: "Close",
  saving: "Saving...",
  save: "Save",
  takePhoto: "Take photo",
  photoClearQuestion: "Is the photo clear and suitable for documentation?",
  uploading: "Uploading...",
  yesSave: "Yes, save",
  camera: {
    appInvoice: "App invoice",
    packedOrder: "Packed order",
    invoice: "Invoice",
    phone: "Phone",
    order: "Order",
    startFailed: "The camera could not start. Choose a photo from the device.",
    prepareFailed: "The photo could not be prepared for upload. Take it again.",
    uploadFailed: "The photo could not be uploaded. Try again.",
    confirmPhoto: "Confirm photo",
    guide: "Photo guide",
    preview: (label) => `${label} preview`,
    progress: (current, total) => `Photo ${current} of ${total}`,
    retake: "Retake",
    starting: "Starting camera...",
    gallery: "▧ Photos",
    switch: "↻ Switch",
  },
};

const EVIDENCE_EN = {
  invoice: { label: "App invoice", guide: "Photograph the whole app invoice clearly inside the frame." },
  order: { label: "Products before packing", guide: "Photograph every product clearly before packing: dishes, sandwiches, drinks and extras." },
  both: { label: "Packed order + app invoice", guide: "Photograph the packed order in its bag with the app invoice clearly in the same photo." },
  courier: { label: "Courier phone + order + invoice", guide: "Show the courier's phone screen, the order and the invoice together." },
};
const CONCLUSION_EN = {
  normal: "No special case",
  rejected: "Rejected",
  cancelled: "Cancelled by the app",
  deliveryManDidntGet: "Courier did not pick up",
};
const REASON_EN = {
  unavailable: "Unavailable",
  prayer_time: "Prayer time",
  order_issue: "Order issue",
  payment_issue: "Payment issue",
  branch_rejected: "Rejected by the branch",
  customer_cancelled: "Cancelled by the customer",
  app_cancelled: "Cancelled by the app",
  driver_no_show: "Courier did not arrive",
  driver_left: "Courier arrived then left",
  driver_late: "Courier was late",
  driver_refused_pickup: "Courier refused the pickup",
  driver_unreachable: "Courier could not be reached",
  other: "Other",
};

export const CASHIER_TEXT = { ar, en };

export function cashierText(lang) {
  const english = lang === "en";
  const t = english ? en : ar;
  const evidence = (type) =>
    english && EVIDENCE_EN[type.key] ? { ...type, ...EVIDENCE_EN[type.key] } : type;
  return {
    lang: english ? "en" : "ar",
    dir: english ? "ltr" : "rtl",
    locale: english ? "en-US" : "ar-SA",
    t,
    evidence,
    evidenceTypes: EVIDENCE_TYPES.map(evidence),
    conclusion: (type) =>
      english ? CONCLUSION_EN[type] || type : conclusionLabel(type),
    reason: (type, value) =>
      english ? REASON_EN[value] || value : conclusionReasonLabel(type, value),
    conclusionOptions: CONCLUSION_OPTIONS.map((option) => ({
      ...option,
      label: english ? CONCLUSION_EN[option.value] || option.label : option.label,
      reasons: option.reasons.map((item) => ({
        ...item,
        label: english ? REASON_EN[item.value] || item.label : item.label,
      })),
    })),
    outcome: (key) => (english ? CONCLUSION_EN[key] : OUTCOME_LABELS[key]) || key,
    // Server errors arrive in English; show a localized message for the scope / permission cases.
    errorMessage: (error) => {
      if (!error) return "";
      if (error.code === "BRANCH_ASSIGNMENT_REQUIRED") return t.restrictedBody;
      if (error.status === 403) return t.forbidden;
      if (error.status === 404) return t.notFound;
      if (error.status === 423) return t.windowExpired;
      if (/delivery scope is not available/i.test(error.message || "")) return t.scopeUnavailable;
      return error.message || t.requestFailed;
    },
  };
}

export function useCashierText() {
  const context = useContext(Context);
  return cashierText(context?.lang);
}
